import { useMemo } from 'react';
import {
  intlNumberFormatter,
  type IntlNumberFormatterOptions,
} from 'react-formatted-input-hook';

import {
  CURRENCIES_META,
  CurrencyCodeType,
} from '@mint-lib/currencies-helpers';
import {
  useProfile,
  type CurrencyFormatType,
  type ProfileLocale,
} from '@mint-lib/profile-helpers';

import { useNumberLocalize } from './locales/useNumberLocalize.js';

export type CurrencyFormatterOptionsType = Omit<
  IntlNumberFormatterOptions,
  'value'
> & {
  currency: CurrencyCodeType;
};

const CURRENCY_FORMAT_MAP: Record<ProfileLocale, CurrencyFormatType> = {
  'en-GB': 'before',
  'en-US': 'before',
  'pt-BR': 'before',
  'it-IT': 'after',
  FAKE: 'after',
};

export const useCurrencyFormatter = () => {
  const locale = useNumberLocalize();
  const { getProfileField } = useProfile();

  const currencyFormat =
    getProfileField('formattingConfig')?.currencyFormat ||
    CURRENCY_FORMAT_MAP[locale as ProfileLocale];

  return useMemo(() => {
    return {
      format: (
        value: IntlNumberFormatterOptions['value'],
        params: CurrencyFormatterOptionsType,
      ) => {
        const currencySymbol = CURRENCIES_META[params.currency].symbol;

        const formatter = intlNumberFormatter({
          locales: locale,
          value,
          liveUpdate: true,
          prefix:
            currencyFormat === 'before' ? `${currencySymbol} ` : undefined,
          suffix: currencyFormat === 'after' ? ` ${currencySymbol}` : undefined,
          minDecimals: 2,
          maxDecimals: 2,
          ...params,
        });

        return formatter.format?.(formatter.onBlur?.(String(value)) ?? '');
      },
    };
  }, [locale, currencyFormat]);
};
